import { inject, provide, type UnwrapNestedRefs, type DeepReadonly, computed } from 'vue'

import usePromptModal from '@/composables/common/modal/usePromptModal'
import useAxiosResponse from '@/composables/common/useAxiosResponse'
import useToast from '@/composables/feedback/useToast'
import useLocalization from '@/composables/localization/useLocalization'
import bookingArmService from '@/services/api/booking'
import { type Booking } from '@/types/Booking'
import { dateTextFormat } from '@/utils/date'

import useBookingListView from './useBookingListView'

interface Props {
  item: Booking | DeepReadonly<Booking>
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export const useBaseBookingListItem = (props: UnwrapNestedRefs<Props>) => {
  const { translationWithFragmentKeys } = useLocalization()
  const { addToast } = useToast()
  const cancelBookingModal = usePromptModal<string>(Symbol('cancel-booking-modal'))
  const { bookingListQuery } = useBookingListView()

  const cancelBookingMutation = useAxiosResponse<unknown, string>(async (bookingStatusReason) => {
    return await bookingArmService.cancelBooking(props.item.id, {
      booking_status_reason: bookingStatusReason
    },
    { requestErrorEnabled: true })
  }, {
    onSuccess () {
      addToast({
        type: 'common',
        message: translationWithFragmentKeys(
          'booking-cancel-success-toast',
          [
            { fragment_key: 'booking ID', value: props.item.booking_no }
          ]),
        second: 5
      })
      bookingListQuery.execute()
    },
    loadingMode: 'normal'
  })

  // #region Getters

  const visitDate = computed((): string => {
    return dateTextFormat(props.item.visit_date)
  })

  const bookingCancelModalParagraph = computed((): string => {
    return translationWithFragmentKeys(
      'booking-cancel-paragraph',
      [
        { fragment_key: 'patient', value: `${props.item.patient_first_name} ${props.item.patient_last_name}` },
        { fragment_key: 'visited-date', value: visitDate.value }
      ])
  })

  // #endregion

  // #region Actions

  const handleCancelBookingClick = async (): Promise<void> => {
    const { isCanceled, data } = await cancelBookingModal.reveal()

    // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
    if (!isCanceled) {
      cancelBookingMutation.execute(data)
    }
  }

  // #endregion

  return {
    cancelBookingModal,
    cancelBookingMutation,
    // Getters
    visitDate,
    bookingCancelModalParagraph,
    // Actions
    handleCancelBookingClick
  }
}

export const defaultKey = Symbol('view:booking-list-item')

export function useBookingListItem (arg1?: Props): ReturnType<typeof useBaseBookingListItem> {
  const result = inject<ReturnType<typeof useBaseBookingListItem> | undefined>(defaultKey, undefined)

  if (result === undefined) {
    if (arg1 === undefined) {
      // NOSONAR
      throw new Error('Require props')
    }

    const newResult = useBaseBookingListItem(arg1)
    provide(defaultKey, newResult)
    return newResult
  }

  return result
}

export default useBookingListItem
